import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient } from '@angular/common/http';
import { Subscriber } from 'rxjs/Subscriber';

import { ClaimAdjusterInfo } from './model/claim-adjuster-info.model';

import { CoreClaimDetail } from './model/core-claim-detail.model';
import { ClaimDatesResult } from './model/claim-dates-result.model';
import { ClaimParty } from './model/claim-party.model';
import { ClaimFeesDisbursementsSummary } from './model/claim-fees-disbursements-summary.model';
import { ClaimDisbursement } from './model/claim-disbursement.model';
import { ClaimFee } from './model/claim-fee.model';

import { global } from '../app.settings';
import { ClaimReservesPaymentsSummary } from './model/claim-reserves-payments-summary.model';
import { ClaimReserve } from './model/claim-reserve.model';
import { ClaimPayment } from './model/claim-payment.model';

import { DataManager } from '@syncfusion/ej2-data';
import { CrawfordApiGridAdaptor, DateRange } from 'crawford-shell';

import { SimpleSearch } from './model/simple-search.model';
import { AdvancedSearch } from './model/advanced-search.model';
import * as moment from 'moment';
import { ProgressStageDetails } from './model/progress-stage-details.model';
import { CodeNamePair } from './model/code-name-pair.model';

@Injectable()
export class ClaimService {

  private savedSearch: SimpleSearch | AdvancedSearch;
  private _claimDataSourceName: string;
  private _claimDataSources: CodeNamePair[];

  constructor(
    private http: HttpClient
  ) {
  }

  public get claimDataSources(): CodeNamePair[] {
    return this._claimDataSources;
  }

  public get claimDataSourceName(): string {
    return this._claimDataSourceName;
  }

  public set claimDataSourceName(value: string) {
    this._claimDataSourceName = value;
  }

  public getClaimAdjusterInfo(crawfordRef: string, source: string): Observable<ClaimAdjusterInfo> {
    return this.http.get<ClaimAdjusterInfo>(global.apiUrl + 'claim/' + crawfordRef + '/adjuster-info?source=' + source);
  }

  public getClaimDates(crawfordRef: string, source: string): Observable<ClaimDatesResult> {
    return this.http.get<ClaimDatesResult>(global.apiUrl + 'claim/' + crawfordRef + '/dates?source=' + source);
  }

  public getClaimComplaintsGrumblesStatus(crawfordRef: string): Observable<string> {
    return this.http.get<string>(global.apiUrl + 'claim/' + crawfordRef + '/complaints-grumbles-status');
  }

  public getClaimById(crawfordRef: string, source: string): Observable<CoreClaimDetail> {
    return this.http.get<CoreClaimDetail>(global.apiUrl + 'claim/' + crawfordRef + '/details?source=' + source);
  }

  public getClaimParties(crawfordRef: string, source: string): Observable<ClaimParty[]> {
    return this.http.get<ClaimParty[]>(global.apiUrl + 'claim/' + crawfordRef + '/parties?source=' + source);
  }

  public getClaimReservesPaymentsSummary(crawfordRef: string, source: string): Observable<ClaimReservesPaymentsSummary> {
    return this.http.get<ClaimReservesPaymentsSummary>(global.apiUrl + 'claim/' + crawfordRef +
      '/reserves-payments-summary?source=' + source);
  }

  public getClaimReserves(crawfordRef: string, source: string): Observable<ClaimReserve[]> {
    return this.http.get<ClaimReserve[]>(global.apiUrl + 'claim/' + crawfordRef + '/reserves?source=' + source);
  }

  public getClaimPayments(crawfordRef: string, source: string): Observable<ClaimPayment[]> {
    return this.http.get<ClaimPayment[]>(global.apiUrl + 'claim/' + crawfordRef + '/payments?source=' + source);
  }

  public getFeesDisbursementsSummary(crawfordRef: string, source: string): Observable<ClaimFeesDisbursementsSummary> {
    return this.http.get<ClaimFeesDisbursementsSummary>(global.apiUrl + 'claim/' + crawfordRef +
      '/fees-disbursements-summary?source=' + source);
  }

  public getClaimDisbursements(crawfordRef: string, source: string): Observable<ClaimDisbursement[]> {
    return this.http.get<ClaimDisbursement[]>(global.apiUrl + 'claim/' + crawfordRef + '/disbursements?source=' + source);
  }

  public getClaimFees(crawfordRef: string, source: string): Observable<ClaimFee[]> {
    return this.http.get<ClaimFee[]>(global.apiUrl + 'claim/' + crawfordRef + '/fees?source=' + source);
  }

  public checkUserHasAccess(crawfordRef: string): Observable<boolean> {
    return this.http.get<boolean>(global.apiUrl + 'user-access/extra-secure-check/' + crawfordRef);
  }

  public checkUserFeesAccess(crawfordRef: string): Observable<boolean> {
    return this.http.get<boolean>(global.apiUrl + 'user-access/fees-access-check/' + crawfordRef);
  }

  public getClaimInactivityStatus(crawfordRef: string): Observable<boolean> {
    return this.http.get<boolean>(global.apiUrl + 'claim/' + crawfordRef + '/inactivity-status');
  }

  public getClaimSearchDataManager(adaptor: CrawfordApiGridAdaptor): DataManager {
    return new DataManager({
      url: global.apiUrl + 'search',
      adaptor: adaptor
    });
  }

  public getAssociatedClaimsDataManager(adaptor: CrawfordApiGridAdaptor): DataManager {
    return new DataManager({
      url: global.apiUrl + 'claim/search/associated-claims',
      adaptor: adaptor
    });
  }

  public getClaimAdjusterFeesDataManager(crawfordRef: string, adaptor: CrawfordApiGridAdaptor): DataManager {
    return new DataManager({
      url: global.apiUrl + 'claim/' + crawfordRef + '/adjuster-fees',
      adaptor: adaptor
    });
  }

  public getAssociatedClaimsForSearch(query: any): Observable<any> {
    return this.http.post<any>(global.apiUrl + 'claim/search/associated-claims', query);
  }

  public getPresentPosition(crawfordRef: string): Observable<string> {
    return this.http.get<string>(global.apiUrl + 'claim/'  + crawfordRef + '/current-position');
  }

  public getProgressStageDetails(crawfordRef: string): Observable<ProgressStageDetails> {
    return this.http.get<ProgressStageDetails>(global.apiUrl + 'claim/'  + crawfordRef + '/progress-stage');
  }

  public getClaimShowPresentPosition(crawfordRef: string): Observable<boolean> {
    return this.http.get<boolean>(global.apiUrl + 'claim/'  + crawfordRef + '/show-current-position');
  }

  public getMediaViewerUrl(crawfordRef: string): Observable<string> {
    return this.http.get<string>(global.apiUrl + 'claim/' + crawfordRef + '/media-viewer-url');
  }

  public loadSearch(): SimpleSearch | AdvancedSearch {
    return this.savedSearch;
  }

  public saveSearch(search: SimpleSearch | AdvancedSearch): void {
    this.savedSearch = search;
  }

  public getClaimDataSources(): Observable<CodeNamePair[]> {
    if (this._claimDataSources) {
      return Observable.create((observer: Subscriber<CodeNamePair[]>) => {
        observer.next(this._claimDataSources);
        observer.complete();
      });
    } else {
      return new Observable(observer => {
        this.http.get<CodeNamePair[]>(global.apiUrl + 'search/filters/claim-data-sources').subscribe(
          claimDataSources => {
            this._claimDataSources = claimDataSources;
            this._claimDataSourceName = this._claimDataSources[0].code;
            observer.next(this._claimDataSources);
            observer.complete();
          }
        );
      });
    }
  }

  mapFormValues(values: any): any {

    const closureDate = this.setInclusiveDateRangeTimeValues(values.closureDateRange);
    const contactDate = this.setInclusiveDateRangeTimeValues(values.contactDateRange);
    const estimatedSettlementDate = this.setInclusiveDateRangeTimeValues(values.estSettlementDateRange);
    const finalReportDate = this.setInclusiveDateRangeTimeValues(values.finalReportDateRange);
    const instructionDate = this.setInclusiveDateRangeTimeValues(values.instructionDateRange);
    const lastReportDate = this.setInclusiveDateRangeTimeValues(values.lastReportDateRange);
    const lossDate = this.setInclusiveDateRangeTimeValues(values.lossDateRange);
    const prelimDate = this.setInclusiveDateRangeTimeValues(values.prelimDateRange);
    const satisfactionDate = this.setInclusiveDateRangeTimeValues(values.satisfactionDateRange);
    const visitDate = this.setInclusiveDateRangeTimeValues(values.visitDateRange);
    const lossStatuses = this.parseCheckboxGroup('lossStatus', values.lossStatusGroup);

    return {
      adjusterCodes : values.leadAdjuster,
      adjusterServiceLineCodes: values.codeTypes === 'LossAdjuster' ? values.serviceLine : null,
      adjusterLossCodes: values.codeTypes === 'LossAdjuster' ? values.codes : null,
      assistingAdjusterCodes: values.assistingAdjuster,
      brokerCodes : values.brokers,
      brokerGroupCodes: values.brokerGroups,
      brokerLocationCodes : values.brokerLocations,
      brokerLossCodes: values.codeTypes === 'Broker' ? values.codes : null,
      brokerReference : values.references === 4 ? values.referenceSearch : null,
      brokerServiceLineCodes: values.codeTypes === 'Broker' ? values.serviceLine : null,
      claimantAddress1 : values.lossAddressType === 'claimant' ? values.lossAddress1 : null,
      claimantName: values.claimantName,
      claimantPostcode : values.lossAddressType === 'claimant' ? values.lossPostcode : null,
      claimDataSource: values.claimDataSource ? values.claimDataSource : this._claimDataSourceName,
      classificationCodes : values.codeClasses,
      clientAccountCode : values.clientAccountSearchType === 1 ? values.clientAccount : null,
      clientAccountName: values.clientAccountSearchType === 2 ? values.clientAccount : null,
      clientAccountSearchOperator: values.clientAccountSearchOperator,
      closureDateFrom: closureDate.startDate,
      closureDateTo: closureDate.endDate,
      delegatedAuthorityScheme: values.delegatedAuthority === 'delegated' ? 'Y' : null,
      estimatedSettlementDateFrom : estimatedSettlementDate.startDate,
      estimatedSettlementDateTo : estimatedSettlementDate.endDate,
      exportFormat: values.exportFormat,
      finalReportDateFrom : finalReportDate.startDate,
      finalReportDateTo : finalReportDate.endDate,
      firstContactDateFrom : values.noFirstContactDate ? null : contactDate.startDate,
      firstContactDateTo : values.noFirstContactDate ? null : contactDate.endDate,
      firstVisitDateFrom : values.noFirstVisitDate ? null : visitDate.startDate,
      firstVisitDateTo : values.noFirstVisitDate ? null : visitDate.endDate,
      includeLinked: values.includeLinked,
      instructionDateFrom : instructionDate.startDate,
      instructionDateTo : instructionDate.endDate,
      insurerCodes : values.insurers,
      insurerGroupCodes: values.insurerGroups,
      insurerLocationCodes : values.insurerLocations,
      insurersClaimNumber : values.references === 2 ? values.referenceSearch : null,
      insurerServiceLineCodes: values.codeTypes === 'Insurer' ? values.serviceLine : null,
      insurerLossCodes: values.codeTypes === 'Insurer' ? values.codes : null,
      lastReportDateFrom: lastReportDate.startDate,
      lastReportDateTo: lastReportDate.endDate,
      lossDateFrom : lossDate.startDate,
      lossDateTo : lossDate.endDate,
      nonDelegatedAuthorityScheme: values.delegatedAuthority === 'nonDelegated' ? 'Y' : null,
      noFirstContactDate : values.noFirstContactDate,
      noPreliminaryReportDate : values.noPreliminaryReportDate,
      noFirstVisitDate : values.noFirstVisitDate,
      packetLossStatuses : lossStatuses,
      packetNumber : values.references === 1 ? values.referenceSearch : null,
      policyHolderAddress1 : values.lossAddressType === 'policyholder' ? values.lossAddress1 : null,
      policyHolderName : values.policyHolderName,
      policyHolderPostcode : values.lossAddressType === 'policyholder' ? values.lossPostcode : null,
      policyNumber : values.references === 3 ? values.referenceSearch : null,
      preliminaryReportDateFrom : values.noPreliminaryReportDate ? null : prelimDate.startDate,
      preliminaryReportDateTo : values.noPreliminaryReportDate ? null : prelimDate.endDate,
      referenceSearchOperator: values.referenceSearchOperator || '0', // default to exact match
      settlementAgreedDateFrom: satisfactionDate.startDate,
      settlementAgreedDateTo: satisfactionDate.endDate,
      technicianCodes : values.leadTechnician
    };
  }

  parseCheckboxGroup(prefix: string, values: any): string[] {
    const selectedValues = [];
    const properties = Object.keys(values);

    properties.forEach(property => {
      if (values[property]) {
        const selectedValue = property.replace(prefix, '');
        selectedValues.push(selectedValue);
      }
    });

    return selectedValues.length === 0 ? null : selectedValues;
  }

  setInclusiveDateRangeTimeValues(dateRange: any): DateRange {
    if (!dateRange || (!dateRange.startDate && !dateRange.endDate)) {
      return new DateRange(null, null);
    }

    return new DateRange(
      moment(dateRange.startDate).toDate(),
      moment(dateRange.endDate).add({ days: 1 }).subtract({ milliseconds: 3 }).toDate()
    );
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/UI/src/app/claim/claim.service.ts